import { Injectable, NgZone, inject } from '@angular/core';
import { setPrefillHitListener } from './prefill-store';

// Bounds ALL offline-prefetch HTTP work to a few concurrent requests and runs it outside
// Angular's zone.
//
// Root cause of a confirmed Android freeze (2026-09-08): OfflinePrefetchService fired ~50-100
// requests in a single tick — the list for each entity, then for EVERY row a detail request +
// an attachments/photos/documents list + one blob download per file, across Business Partners,
// Products, Loading Sites and Transport Units, all fire-and-forget with no throttle. On a slow
// device with a flaky connection every response came back in a clump, and handling each one
// (JSON parse + IndexedDB write + a full Angular change-detection pass over whatever screen was
// mounted) buried the main thread for over a minute — the JS event loop stopped entirely, the
// screen froze, and only a screen lock/unlock (native surface recreation) recovered it. Caught
// on-device via an in-page heartbeat that flat-lined the instant the request burst landed.
//
// Every prefetch HTTP call now goes through run() below: it caps concurrency, runs the work
// (and therefore the completion handlers / IndexedDB writes) outside Angular's zone so it does
// not trigger app-wide change detection, and always yields a macrotask between units so the
// app's own timers, pending input and a frame or two of rendering keep getting slots even when
// the queue is thousands of items deep.
@Injectable({ providedIn: 'root' })
export class PrefetchGateService {
  private readonly zone = inject(NgZone);

  constructor() {
    // A prefetch request that the HTTP layer answers from the prefill store never reaches the server, so it must not use up the
    // per-minute request budget (it was counted when it started): give that one back.
    setPrefillHitListener(() => { this.startedAt.pop(); });
  }

  // Keeps a reconnect sync moving at a reasonable pace while leaving the main thread, the
  // browser's own ~6-per-host socket pool and the render pipeline plenty of headroom. This is a
  // safety limit, not a tuning knob.
  private static readonly MAX_CONCURRENT = 3;

  // Base pause after each unit so timers / input / rendering always get a slot in between. A
  // unit that itself blocked the main thread for a while (a big JSON parse, a large IndexedDB
  // write) gets a proportionally longer pause — see run().
  private static readonly YIELD_MS = 40;
  private static readonly SLOW_UNIT_MS = 350;
  private static readonly SLOW_UNIT_YIELD_MS = 300;

  // 2026-10-02: a REQUEST BUDGET on top of the concurrency cap. The server allows 200 API requests per minute per IP address (RateLimitFilter) and then
  // answers 429 to EVERYTHING from that address. A first sync after login warms up to 300 transport units x about 6 calls each, three at a time as fast as
  // they come back: that is well over 200 in a minute, so the app starved its own normal calls (the quality-grade list, permissions, heartbeat ...) with 429
  // and showed a "Too Many Requests" toast for each. Now the prefetch starts at most BUDGET requests in any 60 seconds (the rest of the quota stays free for
  // the person using the app and for other devices behind the same address), and a 429 from the server pauses it for a minute. A full first sync is slower
  // (a few minutes more for a big window); it is a background warm-up, and anything the person opens is fetched directly.
  private static readonly BUDGET = 100;
  private static readonly WINDOW_MS = 60_000;
  private readonly startedAt: number[] = [];
  private blockedUntil = 0;
  private pumpTimer: ReturnType<typeof setTimeout> | null = null;

  private active = 0;
  private readonly queue: Array<() => void> = [];

  // Bumped by cancelPending(). A unit that was queued under an older epoch is skipped without calling the
  // server (like pause()), and a unit that was already running under an older epoch no longer counts against
  // `active` when it ends — so a request that never answers (dead session) cannot hold a slot forever.
  private epoch = 0;

  /** Session ended / identity changing: drop everything still waiting (callers get `undefined`, as with pause())
   *  and free the slots. Requests already on the wire are not aborted; their results are ignored by the callers
   *  that check the sync run id. */
  cancelPending(): void {
    this.epoch++;
    this.active = 0;
    // Resolve every waiting unit now (each one sees its old epoch and skips the server) — going through pump()
    // would hold them back behind the request budget / 429 pause, which is the wrong thing to wait for here.
    const waiting = this.queue.splice(0, this.queue.length);
    waiting.forEach((start) => start());
  }

  // When paused, queued units resolve immediately WITHOUT executing (still releasing their
  // slot). OfflinePrefetchService flips this from a slowConnection() effect — a prefetch is a
  // best-effort cache warm; on a connection too slow to handle it, doing nothing is correct.
  private paused = false;
  pause(): void { this.paused = true; }
  resume(): void { this.paused = false; this.pump(); }

  // Runs `task` once a concurrency slot is free, OUTSIDE Angular's zone, then yields a macrotask
  // before releasing the slot. Only the task's own rejection propagates to the caller (callers
  // already swallow those per the prefetch "best effort" convention); waiting for a slot never
  // rejects.
  run<T>(task: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const queuedEpoch = this.epoch;
      const start = (): void => {
        this.active++;
        const startEpoch = this.epoch;
        if (this.paused || queuedEpoch !== startEpoch) {
          // Skip execution entirely; still release the slot (after a yield) so the queue drains.
          // (Also used for a unit queued before cancelPending(): the session it belonged to has ended.)
          resolve(undefined as T);
          setTimeout(() => { if (startEpoch === this.epoch) this.active--; this.pump(); }, PrefetchGateService.YIELD_MS);
          return;
        }
        this.startedAt.push(Date.now());   // counts against the budget (a paused unit does not call the server, so it is not counted)
        this.zone.runOutsideAngular(() => {
          const startedAt = Date.now();
          Promise.resolve()
            .then(task)
            .catch((err) => {
              // the server said 429: stop starting new requests until its window is over
              if ((err as any)?.status === 429) this.blockedUntil = Date.now() + PrefetchGateService.WINDOW_MS;
              throw err;
            })
            .then(resolve, reject)
            .finally(() => {
              const blockedish = Date.now() - startedAt;
              const wait = blockedish > PrefetchGateService.SLOW_UNIT_MS
                ? PrefetchGateService.SLOW_UNIT_YIELD_MS
                : PrefetchGateService.YIELD_MS;
              setTimeout(() => {
                if (startEpoch === this.epoch) this.active--;
                this.pump();
              }, wait);
            });
        });
      };
      this.queue.push(start);
      this.pump();
    });
  }

  // Run `task` for every item, gated. Resolves once all have settled — a rejected task never
  // aborts its siblings.
  async mapGated<T>(items: readonly T[], task: (item: T) => Promise<unknown>): Promise<void> {
    await Promise.allSettled(items.map((item) => this.run(() => Promise.resolve(task(item)))));
  }

  private pump(): void {
    while (this.active < PrefetchGateService.MAX_CONCURRENT && this.queue.length > 0) {
      if (!this.paused) {
        const now = Date.now();
        if (now < this.blockedUntil) { this.pumpLater(this.blockedUntil - now); return; }
        while (this.startedAt.length && now - this.startedAt[0] >= PrefetchGateService.WINDOW_MS) this.startedAt.shift();
        if (this.startedAt.length >= PrefetchGateService.BUDGET) {
          this.pumpLater(PrefetchGateService.WINDOW_MS - (now - this.startedAt[0]) + 50);
          return;
        }
      }
      const next = this.queue.shift()!;
      next();
    }
  }

  private pumpLater(ms: number): void {
    if (this.pumpTimer) return;
    this.pumpTimer = setTimeout(() => { this.pumpTimer = null; this.pump(); }, Math.max(50, ms));
  }
}
